import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { User } from 'lucide-react';
import { getApiUrl } from '@/config';
import type { Log } from '@shared/types';

interface AssignmentTimelineProps {
    assignmentId: string;
}

export const AssignmentTimeline: React.FC<AssignmentTimelineProps> = ({ assignmentId }) => {
    const [logs, setLogs] = useState<Log[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchLogs = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    setLoading(false);
                    return;
                }

                const res = await fetch(getApiUrl(`/api/assignments/${assignmentId}/logs`), {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!res.ok) {
                    throw new Error('Impossible de charger l\'historique');
                }

                const data = await res.json();
                setLogs(data);
            } catch (err: any) {
                console.error(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (assignmentId) {
            fetchLogs();
        }
    }, [assignmentId]);

    if (loading) return <div className="text-center p-4 text-muted animate-pulse">Chargement de l'historique...</div>;

    // Silent fail if error to not break modal, just show message
    if (error) return <div className="glass-panel p-4 text-center text-red-400 text-sm">Erreur de chargement de l'historique.</div>;

    if (logs.length === 0) return (
        <div className="glass-panel p-8 text-center">
            <p className="text-muted">Aucune activité enregistrée pour ce chantier.</p>
        </div>
    );

    return (
        <div className="space-y-6 relative pl-4 border-l-2 border-white/5 ml-2 mt-4">
            {logs.map((log) => (
                <div key={log.id} className="relative">
                    {/* Timeline Dot */}
                    <div className={`absolute -left-[21px] top-1 w-3 h-3 rounded-full border-2 border-[#1e293b] ${log.action === 'DELETE_ASSIGNMENT' ? 'bg-red-500' :
                            log.action === 'CREATE_ASSIGNMENT' ? 'bg-emerald-500' : 'bg-blue-500'
                        }`}></div>

                    <div className="glass-panel p-3 text-sm hover:bg-white/5 transition-colors">
                        <div className="flex items-center justify-between gap-2 mb-2">
                            <span className="font-semibold text-main">
                                {log.action === 'UPDATE_ASSIGNMENT' ? 'Mise à jour' :
                                    log.action === 'DELETE_ASSIGNMENT' ? 'Suppression' :
                                        log.action === 'CREATE_ASSIGNMENT' ? 'Création' : log.action}
                            </span>
                            <span className="text-xs text-muted font-mono bg-white/5 px-2 py-0.5 rounded">
                                {format(parseISO(log.timestamp), 'dd MMM HH:mm', { locale: fr })}
                            </span>
                        </div>

                        <div className="flex items-center gap-2 text-xs text-muted mb-3 pb-2 border-b border-white/5">
                            <User size={12} />
                            <span>{log.username || 'Système'}</span>
                            {log.ip && <span className="text-gray-600">• IP: {log.ip.split(',')[0]}</span>}
                        </div>

                        {/* Details Changes */}
                        {log.details && Object.keys(log.details).length > 0 && (
                            <div className="space-y-1">
                                {Object.entries(log.details).map(([key, value]) => {
                                    if (key === 'updatedAt') return null; // Skip redundant fields

                                    // Format Value
                                    let displayValue = String(value);
                                    if (typeof value === 'object') displayValue = JSON.stringify(value);
                                    if (displayValue.length > 50) displayValue = displayValue.substring(0, 50) + '...';

                                    // Translate common keys
                                    const label = {
                                        status: 'Statut',
                                        technicianId: 'Technicien',
                                        startDate: 'Date Début',
                                        endDate: 'Date Fin',
                                        notes: 'Notes',
                                        remarque: 'Remarque'
                                    }[key] || key;

                                    return (
                                        <div key={key} className="flex items-start gap-2 text-xs font-mono">
                                            <span className="text-accent min-w-[80px] text-right opacity-70">{label}:</span>
                                            <span className="text-gray-300 break-all">{displayValue}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};
